import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const MONTH_TENOR_REGEX = /^[A-Z][a-z]{2}-\d{2}$/;
const QUARTER_TENOR_REGEX = /^Q[1-4]-\d{2}$/i;
const SEASON_TENOR_REGEX = /^(Win|Sum|Spr|Aut)-\d{2}$/i;
const CALENDAR_TENOR_REGEX = /^Cal-\d{2}(\s*\([A-Za-z]{3}\))?$/i;

export function isMonthTenorLabel(label) {
  return MONTH_TENOR_REGEX.test(String(label ?? "").trim());
}

export function isCalendarTenorLabel(label) {
  return CALENDAR_TENOR_REGEX.test(String(label ?? "").trim());
}

export function normalizePeriodLabel(periodLabel) {
  return String(periodLabel ?? "")
    .trim()
    .toLowerCase();
}

export function isApoStyle(styleLabel, productLabel, shortcutLabel = "") {
  const hay = `${styleLabel ?? ""} ${productLabel ?? ""} ${shortcutLabel ?? ""}`
    .toLowerCase();
  return hay.includes("apo");
}

export function getPeriodFormatHint(periodLabel) {
  const period = normalizePeriodLabel(periodLabel);
  if (period.includes("month")) return "e.g. Dec-25";
  if (period.includes("quarter")) return "e.g. Q1-26";
  if (period.includes("season")) return "e.g. Win-26";
  if (period.includes("calendar") || period.includes("cal")) return "e.g. Cal-26 or Cal-26 (Mar)";
  return "";
}

/** Fallback month count when the missing-tenors API omits `sublegs`. */
export function defaultMonthsInTenorForPeriod(periodLabel) {
  const period = normalizePeriodLabel(periodLabel);
  if (period.includes("quarter")) return 3;
  if (period.includes("season")) return 3;
  if (period.includes("calendar") || period.includes("cal")) return 12;
  return 1;
}

export function validateSublegMonthLabel(sublegLabel) {
  const label = String(sublegLabel ?? "").trim();
  if (!label) return "Sub-leg is required";
  if (!isMonthTenorLabel(label)) {
    return "Sub-leg must look like Dec-25";
  }
  return null;
}

export function validateTenorLabelForPeriod(tenorLabel, periodLabel) {
  const label = String(tenorLabel ?? "").trim();
  if (!label) return "Tenor is required";

  const period = normalizePeriodLabel(periodLabel);
  if (period.includes("month") && !MONTH_TENOR_REGEX.test(label)) {
    return "Month tenors must look like Dec-25";
  }
  if (period.includes("quarter") && !QUARTER_TENOR_REGEX.test(label)) {
    return "Quarter tenors must look like Q1-26";
  }
  if (period.includes("season") && !SEASON_TENOR_REGEX.test(label)) {
    return "Season tenors must look like Win-26";
  }
  if (
    (period.includes("calendar") || period.includes("cal")) &&
    !isCalendarTenorLabel(label)
  ) {
    return "Calendar tenors must look like Cal-26 or Cal-26 (Mar)";
  }
  return null;
}

export function parseMonthTenor(tenorLabel) {
  const m = String(tenorLabel).trim().match(/^([A-Z][a-z]{2})-(\d{2})$/);
  if (!m) return null;
  const parsed = dayjs(`${m[1]}-01-20${m[2]}`, "MMM-DD-YYYY", true);
  return parsed.isValid() ? parsed : null;
}

export function compareMonthTenors(a, b) {
  const da = parseMonthTenor(a);
  const db = parseMonthTenor(b);
  if (!da || !db) return String(a).localeCompare(String(b));
  return da.valueOf() - db.valueOf();
}

/**
 * @param {string[]} existingTenorLabels
 * @param {string} periodLabel
 */
export function getLatestExistingMonthTenor(existingTenorLabels, periodLabel) {
  if (!normalizePeriodLabel(periodLabel).includes("month")) return null;
  const monthTenors = existingTenorLabels.filter((t) => MONTH_TENOR_REGEX.test(t));
  if (!monthTenors.length) return null;
  return monthTenors.sort(compareMonthTenors).at(-1);
}

/**
 * @param {string} latestLabel e.g. Aug-26
 */
export function nextMonthTenorLabel(latestLabel) {
  const d = parseMonthTenor(latestLabel);
  if (!d) return null;
  return d.add(1, "month").format("MMM-YY");
}

function parseQuarterTenor(label) {
  const m = String(label).trim().match(/^Q([1-4])-(\d{2})$/i);
  if (!m) return null;
  const year = 2000 + Number(m[2]);
  return { quarter: Number(m[1]), year };
}

function compareQuarterTenors(a, b) {
  const qa = parseQuarterTenor(a);
  const qb = parseQuarterTenor(b);
  if (!qa || !qb) return String(a).localeCompare(String(b));
  if (qa.year !== qb.year) return qa.year - qb.year;
  return qa.quarter - qb.quarter;
}

function nextQuarterTenorLabel(latestLabel) {
  const q = parseQuarterTenor(latestLabel);
  if (!q) return null;
  if (q.quarter < 4) return `Q${q.quarter + 1}-${String(q.year).slice(-2)}`;
  return `Q1-${String(q.year + 1).slice(-2)}`;
}

export function suggestDatesForTenor(tenorLabel, periodLabel, apo) {
  const period = normalizePeriodLabel(periodLabel);
  const label = String(tenorLabel ?? "").trim();

  if (period.includes("month")) {
    const d = parseMonthTenor(label);
    if (!d) return { start_date: null, end_date: null };
    return {
      start_date: apo ? d.startOf("month") : null,
      end_date: d.endOf("month"),
    };
  }

  if (period.includes("quarter")) {
    const q = parseQuarterTenor(label);
    if (!q) return { start_date: null, end_date: null };
    const start = dayjs(new Date(q.year, (q.quarter - 1) * 3, 1));
    const end = start.add(3, "month").subtract(1, "day");
    return {
      start_date: apo ? start : null,
      end_date: end,
    };
  }

  return { start_date: null, end_date: null };
}

/**
 * @param {string[]} existingLabels
 * @param {string} periodLabel
 * @param {{ label: string }[]} availableOptions
 */
export function getTenorAvailabilityHint(existingLabels, periodLabel, availableOptions) {
  const period = normalizePeriodLabel(periodLabel);
  const count = availableOptions?.length ?? 0;
  if (!count) {
    return existingLabels.length
      ? "No new tenors available for this period (all may already exist)."
      : "Select PCS and period to load tenors.";
  }

  if (period.includes("month")) {
    const latest = getLatestExistingMonthTenor(existingLabels, periodLabel);
    const next = latest ? nextMonthTenorLabel(latest) : availableOptions[0]?.label;
    return latest
      ? `${count} tenor(s) available. Latest live: ${latest}. Next suggested: ${next}.`
      : `${count} tenor(s) available.`;
  }

  if (period.includes("quarter")) {
    const quarters = existingLabels.filter((t) => QUARTER_TENOR_REGEX.test(t));
    const latest = quarters.length
      ? quarters.sort(compareQuarterTenors).at(-1)
      : null;
    const next = latest ? nextQuarterTenorLabel(latest) : availableOptions[0]?.label;
    return latest
      ? `${count} tenor(s). Latest live: ${latest}. Next: ${next}.`
      : `${count} tenor(s) available.`;
  }

  return `${count} tenor(s) available (existing live tenors hidden).`;
}

/**
 * @param {{ value: number, label: string }[]} allOptions
 * @param {string[]} existingLabels
 * @param {string} periodLabel
 */
export function filterTenorOptionsAfterExisting(
  allOptions,
  existingLabels,
  periodLabel
) {
  if (!Array.isArray(allOptions) || !allOptions.length) return [];

  const period = normalizePeriodLabel(periodLabel);
  const existingSet = new Set(existingLabels.map((t) => String(t).trim()));

  if (period.includes("month")) {
    const latest = getLatestExistingMonthTenor(existingLabels, periodLabel);
    const minLabel = latest ? nextMonthTenorLabel(latest) : null;
    if (!minLabel) {
      return allOptions.filter((o) => !existingSet.has(String(o.label).trim()));
    }
    return allOptions.filter((o) => {
      const label = String(o.label).trim();
      if (existingSet.has(label)) return false;
      return compareMonthTenors(label, minLabel) >= 0;
    });
  }

  if (period.includes("quarter")) {
    const quarters = existingLabels.filter((t) => QUARTER_TENOR_REGEX.test(t));
    const latest = quarters.length
      ? quarters.sort(compareQuarterTenors).at(-1)
      : null;
    const minLabel = latest ? nextQuarterTenorLabel(latest) : null;
    if (!minLabel) {
      return allOptions.filter((o) => !existingSet.has(String(o.label).trim()));
    }
    return allOptions.filter((o) => {
      const label = String(o.label).trim();
      if (existingSet.has(label)) return false;
      return compareQuarterTenors(label, minLabel) >= 0;
    });
  }

  return allOptions.filter((o) => !existingSet.has(String(o.label).trim()));
}

function monthLabelsFromAnchor(anchorDayjs, count) {
  return Array.from({ length: count }, (_, i) =>
    anchorDayjs.add(i, "month").format("MMM-YY")
  );
}

/**
 * @param {object} [styleContext]
 * @param {string} [styleContext.styleLabel]
 * @param {string} [styleContext.productLabel]
 * @param {string} [styleContext.shortcutLabel]
 */
export function getAddTenorStyleContext(row, shortcutLabel = "") {
  return {
    styleLabel: row.style_label ?? row.style_display ?? "",
    productLabel: row.product_label ?? "",
    shortcutLabel,
  };
}

/**
 * Autofill sub-leg labels from period, style, and parent tenor.
 * @param {string} tenorLabel
 * @param {number} monthsCount
 * @param {string} periodLabel
 * @param {ReturnType<typeof getAddTenorStyleContext>} [styleContext]
 * @returns {string[]}
 */
export function autofillSubLegs(
  tenorLabel,
  monthsCount,
  periodLabel,
  styleContext = {}
) {
  const count = Math.max(1, Number(monthsCount) || 1);
  if (count === 1) return [];

  const tenor = String(tenorLabel ?? "").trim();
  const period = normalizePeriodLabel(periodLabel);
  const styleLabel = styleContext.styleLabel ?? "";
  const productLabel = styleContext.productLabel ?? "";
  const shortcutLabel = styleContext.shortcutLabel ?? "";
  const apo = isApoStyle(styleLabel, productLabel, shortcutLabel);

  if (period.includes("month")) {
    const start = parseMonthTenor(tenor);
    if (start) return monthLabelsFromAnchor(start, count);
  }

  if (period.includes("quarter")) {
    const q = parseQuarterTenor(tenor);
    if (q) {
      const quarterStart = dayjs(new Date(q.year, (q.quarter - 1) * 3, 1));
      if (apo || count === 3) {
        return monthLabelsFromAnchor(quarterStart, count);
      }
      return Array.from({ length: count }, () => tenor);
    }
  }

  if (tenor) {
    return Array.from({ length: count }, () => tenor);
  }

  return Array.from({ length: count }, (_, i) => `Sub-leg ${i + 1}`);
}

/** @param {object} row @param {string} [shortcutLabel] */
export function autofillSubLegsForRow(row, shortcutLabel = "") {
  const months = Math.max(1, Number(row.months_in_tenor) || 1);
  if (months <= 1 || !String(row.tenor_label ?? "").trim()) return [];
  return autofillSubLegs(
    row.tenor_label,
    months,
    row.period_label,
    getAddTenorStyleContext(row, shortcutLabel)
  );
}

export function mapTenorApiOptions(response) {
  const nested = response?.data?.data?.data;
  const rows = Array.isArray(nested)
    ? nested
    : Array.isArray(nested?.data)
      ? nested.data
      : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

  return rows
    .map((item) => {
      const value =
        item?.id ??
        item?.style_period_tenor_id ??
        item?.value ??
        item?.period_type_id;
      const label =
        item?.title ?? item?.label ?? item?.tenor ?? item?.name ?? "";
      if (value == null || !label) return null;
      return { value: Number(value), label: String(label) };
    })
    .filter(Boolean);
}

function collectTenorLabelFromRow(row, labels) {
  if (!row || typeof row !== "object") return;
  const t =
    row.tenor ??
    row.tenor_label ??
    row.label ??
    row.title ??
    row.name;
  if (typeof t === "string" && t.trim()) labels.add(t.trim());
}

function rowsFromVolSurfaceResponse(tableRowsResponse) {
  const root = tableRowsResponse?.data?.data;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.main_control_table)) return root.main_control_table;
  if (Array.isArray(root?.data)) return root.data;
  return [];
}

/**
 * Keep only labels that match the active period type (month / quarter / etc.).
 * @param {string[]} existingLabels
 * @param {string} periodLabel
 */
export function filterExistingLabelsForPeriod(existingLabels, periodLabel) {
  const period = normalizePeriodLabel(periodLabel);
  if (!period) return existingLabels;

  if (period.includes("month")) {
    return existingLabels.filter((t) => MONTH_TENOR_REGEX.test(t));
  }
  if (period.includes("quarter")) {
    return existingLabels.filter((t) => QUARTER_TENOR_REGEX.test(t));
  }
  if (period.includes("season")) {
    return existingLabels.filter((t) => SEASON_TENOR_REGEX.test(t));
  }
  if (period.includes("calendar") || period.includes("cal")) {
    return existingLabels.filter((t) => isCalendarTenorLabel(t));
  }
  return existingLabels;
}

/**
 * @param {unknown} tableRowsResponse
 * @returns {string[]}
 */
export function extractExistingTenorLabels(tableRowsResponse) {
  const rows = rowsFromVolSurfaceResponse(tableRowsResponse);
  const labels = new Set();
  for (const row of rows) {
    collectTenorLabelFromRow(row, labels);
    if (Array.isArray(row?.children)) {
      for (const child of row.children) collectTenorLabelFromRow(child, labels);
    }
  }
  return [...labels];
}
